import { DEVICE, TYPOGRAPHY } from '@net0-calc/common/assets/styles';
import { CrossIcon } from '@net0-calc/common/assets/svg';
import React, { FC, useEffect, useRef, useState } from 'react';
import { FieldRenderProps } from 'react-final-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'services';
import styled from 'styled-components/macro';
import { containsOnlyLatinChars } from 'utils';

type Suggestion = { name: string; country: string; iata: string };

// some search api
const SearchApi = {
  searchPort: async (value: string): Promise<Suggestion[]> => {
    return [{ name: value, country: value, iata: value }];
  },
};

interface SearchAirportInputProps extends FieldRenderProps<string> {
  className?: string;
  isDisabled?: boolean;
  label?: string;
  placeholder?: string;
}

const useSearchAirports = ({ initialValue }: { initialValue: string }) => {
  const [value, setValue] = useState(initialValue);
  const [shouldFetchData, setShouldFetchData] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [suggestions, setSuggestions] = useState<Suggestion[] | null>(null);
  const clearSuggestions = () => setSuggestions(null);

  useEffect(() => {
    if (!shouldFetchData) {
      return;
    }

    const minSearchValueLen = 3;
    const maxSearchValueLen = 25;
    if (value.length < minSearchValueLen) return;
    if (value.length > maxSearchValueLen) return;

    let didCancel = false;
    let timeoutId: NodeJS.Timeout | undefined;

    const doFetch = async () => {
      setIsLoading(true);
      try {
        const $suggestions = await SearchApi.searchPort(value);
        if (didCancel) return;

        setSuggestions($suggestions);
      } catch (error) {
        if (didCancel) return;
        toast.error((error as Error)?.message);
      } finally {
        setIsLoading(false);
      }
    };

    const debounceTimeMs = 300;
    setTimeout(doFetch, debounceTimeMs);

    return () => {
      didCancel = true;
      clearTimeout(timeoutId);
    };
  }, [value, shouldFetchData]);

  return {
    value,
    setValue: (value: string, shouldFetchData = true) => {
      setValue(value);
      setShouldFetchData(shouldFetchData);
    },
    suggestions,
    clearSuggestions,
    isLoading,
  };
};

export const SearchAirportInput: FC<SearchAirportInputProps> = ({ className, label, placeholder, input }) => {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);

  const airportsSearcher = useSearchAirports({ initialValue: input.value });

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const targetValue = event.currentTarget.value;

    if (targetValue.length === 0 || containsOnlyLatinChars(targetValue)) {
      airportsSearcher.setValue(targetValue);
    }
  };

  const onSelectItem = (suggestion: Suggestion) => async () => {
    inputRef.current && inputRef.current.blur();

    const value = suggestion.iata;

    airportsSearcher.setValue(value, false);
    airportsSearcher.clearSuggestions();

    input.onChange(value);
  };

  const handleClear = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    airportsSearcher.setValue('');
    input.onChange(undefined);
  };

  const { suggestions } = airportsSearcher;
  return (
    <Root className={className}>
      <StyledLabel>{label}</StyledLabel>
      <InputWrap>
        <StyledInput
          ref={inputRef}
          value={airportsSearcher.value}
          onChange={handleChange}
          placeholder={placeholder}
          autoComplete="off"
        />
        {airportsSearcher.value && (
          <ClearButton tabIndex={-1} type="button" onClick={handleClear}>
            <CrossIcon />
          </ClearButton>
        )}
        {airportsSearcher.isLoading && (
          <SearchResultList>
            <SearchResultListItem>{t('common.search.loading')}</SearchResultListItem>
          </SearchResultList>
        )}
        {!airportsSearcher.isLoading && suggestions && (
          <SearchResultList>
            {suggestions.length === 0 && <SearchEmptyResultItem>{t('common.search.noResults')}</SearchEmptyResultItem>}
            {suggestions.map(suggestion => {
              const { iata, name, country } = suggestion;

              return (
                <SearchResultListItem key={iata} onClick={onSelectItem(suggestion)}>
                  <strong>{iata}</strong> (<small>{country}</small>, <small>{name}</small>)
                </SearchResultListItem>
              );
            })}
          </SearchResultList>
        )}
      </InputWrap>
    </Root>
  );
};

const Root = styled.div`
  width: 100%;
`;

const StyledInput = styled.input`
  ${TYPOGRAPHY.bodyRegular16}
  width: 80%;
  color: var(--color6);
  outline: none;
`;

const InputWrap = styled.div`
  position: relative;
  width: 100%;
  height: 64px;
  padding: 18px;

  border-radius: 15px;
  border: 2px solid var(--color10);

  background-color: var(--color5);

  cursor: text;
  transition: all 0.2s;
  margin-bottom: 24px;

  @media ${DEVICE.laptop} {
    margin-bottom: 0px;
  }

  &:focus-within {
    border: 2px solid var(--color2);
  }
`;

const SearchResultList = styled.ul`
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  z-index: 500;
  border-radius: 8px;
  box-shadow: 0px 5px 10px var(--color10);

  width: 100%;
  max-height: 230px;
  background-color: var(--color5);

  margin: 0;
  padding: 0;
  overflow: auto;
`;

const SearchResultListItem = styled.li`
  ${TYPOGRAPHY.bodyMedium20};
  color: var(--color6);

  width: 100%;
  padding: 16px;

  cursor: pointer;

  &:not(:last-child) {
    border-bottom: 1px solid var(--color10);
  }

  &:hover {
    background-color: var(--color8);
  }
`;

const SearchEmptyResultItem = styled.li`
  ${TYPOGRAPHY.bodyMedium20};
  color: var(--color6);
  width: 100%;
  padding: 16px;
  text-align: center;
`;

const StyledLabel = styled.label`
  ${TYPOGRAPHY.bodyRegular16};
  display: block;
  margin-bottom: 8px;
  color: var(--color9);
`;

const ClearButton = styled.button`
  position: absolute;
  top: 18px;
  right: 18px;
  cursor: pointer;
`;
